import Vue from 'vue'
import Router from 'vue-router'
import store from '@/store'

// IB Views
import IBLogin from './views/IBLogin.vue'
import CommunitySelector from './components/CommunitySelector.vue'
import IBDashboard from './views/IBDashboard.vue'

// Reuse trader's Club components
import ClubEvents from '@/views/Community/components/ClubEvents.vue'
import ClubMembers from '@/views/Community/components/ClubMembers.vue'
import ClubTournaments from '@/views/Community/components/ClubTournaments.vue'
import ClubLeague from '@/views/Community/components/ClubLeague.vue'
import ClubSettings from '@/views/Community/components/ClubSettings.vue'

// Reuse trader views
import NewJournal from '@/views/Journal/views/NewJournal.vue'
import TradeList from '@/views/Journal/TradeList.vue'
import JournalStats from '@/views/Journal/views/JournalStats'
import CalendarIndex from '@/views/Calendar/CalendarIndex'
import SimulationsIndex from '@/views/Funding/Simulation/views/SimulationsIndex.vue'
import NewFrontPage from '@/views/Funding/Simulation/views/NewFrontPage.vue'
import FundingSimulationOnBoarding from '@/views/Funding/Simulation/views/TestOnBoarding'
import FundingSimulationSummary from '@/views/Funding/Simulation/views/TestOverview'
import FundingSimulationFeedback from '@/views/Funding/Simulation/views/TestFeedback'
import Wizard from '@/views/Funding/Simulation/components/Wizard/Wizard.vue'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: '/',
  routes: [
    // Public — login
    {
      path: '/login',
      name: 'ib-login',
      component: IBLogin,
      meta: { public: true }
    },

    // Community selection (requires auth, no active community needed)
    {
      path: '/communities',
      name: 'ib-communities',
      component: CommunitySelector,
      meta: { requiresAuth: true, skipCommunityCheck: true }
    },

    // Community-scoped routes
    {
      path: '/dashboard',
      name: 'ib-dashboard',
      component: IBDashboard,
      meta: { requiresAuth: true, title: 'Dashboard' }
    },
    {
      path: '/events',
      name: 'ib-events',
      component: ClubEvents,
      meta: { requiresAuth: true, title: 'Events' }
    },
    {
      path: '/members',
      name: 'ib-members',
      component: ClubMembers,
      meta: { requiresAuth: true, title: 'Members' }
    },
    {
      path: '/competitions',
      name: 'ib-competitions',
      component: ClubTournaments,
      meta: { requiresAuth: true, title: 'Competition' }
    },
    {
      path: '/leaderboard',
      name: 'ib-leaderboard',
      component: ClubLeague,
      meta: { requiresAuth: true, title: 'Leaderboard' }
    },
    {
      path: '/settings',
      name: 'ib-settings',
      component: ClubSettings,
      meta: { requiresAuth: true, title: 'Settings' }
    },

    // Trader views — Journal
    {
      path: '/journal',
      name: 'ib-journal',
      component: NewJournal,
      meta: { requiresAuth: true, title: 'Journal' },
      children: [
        { path: 'data', name: 'ib-journal-data', component: TradeList, props: true },
        { path: 'stats', name: 'ib-journal-stats', component: JournalStats, props: true },
        { path: 'calendar', name: 'ib-journal-calendar', component: CalendarIndex }
      ]
    },

    // Trader views — Funding Simulation
    {
      path: '/funding',
      component: SimulationsIndex,
      meta: { requiresAuth: true, title: 'Funding' },
      children: [
        { path: '', redirect: 'home' },
        { path: 'home', name: 'funding-simulation-home', component: NewFrontPage, props: true },
        { path: 'on-boarding', name: 'funding-simulation-on-boarding', component: FundingSimulationOnBoarding, props: true },
        { path: 'summary', name: 'funding-simulation-summary', component: FundingSimulationSummary, props: true },
        { path: 'feedback', name: 'funding-simulation-feedback', component: FundingSimulationFeedback, props: true },
        { path: 'wizard', name: 'funding-simulation-wizard', component: Wizard, props: true }
      ]
    },

    // Catch-all
    {
      path: '*',
      redirect: '/dashboard'
    }
  ]
})

// Navigation guards
router.beforeEach((to, from, next) => {
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const isPublic = to.matched.some(record => record.meta.public)
  const skipCommunityCheck = to.matched.some(record => record.meta.skipCommunityCheck)

  // Check if user is authenticated (token in global store)
  const isAuthenticated = !!store.state.token && store.state.user.id !== 0

  // Check if user has selected a community
  const hasCommunity = store.getters['ib/hasCommunity']

  if (requiresAuth && !isAuthenticated) {
    // Not logged in — redirect to login
    next({ path: '/login', query: { redirect: to.fullPath } })
  } else if (requiresAuth && !skipCommunityCheck && !hasCommunity) {
    // Logged in but no community selected — redirect to community selector
    next('/communities')
  } else if (isPublic && isAuthenticated) {
    // Already logged in, redirect away from login
    if (hasCommunity) {
      next('/dashboard')
    } else {
      next('/communities')
    }
  } else {
    next()
  }
})

// After navigation — update document title
router.afterEach((to) => {
  const partnerName = store.getters['ib/partnerName'] || 'IB Space'
  document.title = to.meta.title ? `${to.meta.title} - ${partnerName}` : partnerName
})

export default router
